import React from 'react';
import { parseISO, format } from 'date-fns';
import { MONTH_DAY_YEAR_FORMAT } from '@/Framework/dateHelpers/dateFormats';
import Tooltip from '@dealroadshow/uikit/core/components/Tooltip';
import formatNumber from '@/Framework/dataHelpers/formatters/formatNumber';
import {
  UNDERWRITER_NAME,
  RATING_AGENCY_NAME,
} from '@/finsight/ui/common/components/TopCompanies/constants';

import styles from './styles.scss';

const getNameCell = ({ rowData: { name } }) => (
  <Tooltip
    content={ name }
    containerClassName={ styles.name }
  >
    { name }
  </Tooltip>
);

const getDealCount = ({ rowData: { dealCount } }) => (
  <span className={ styles.dealCountWrp }>
    <Tooltip content="# of Issuances">
      <span className={ styles.dealCount }>{ dealCount }</span>
    </Tooltip>
  </span>
);

const getIssueDate = ({ rowData: { latestDealDate } }) => {
  const parsedDate = parseISO(new Date(latestDealDate).toISOString());
  return (
    <span className={ styles.date }>
      <Tooltip content="Last Issuance Date">
        <span>{ format(parsedDate, MONTH_DAY_YEAR_FORMAT) }</span>
      </Tooltip>
    </span>
  );
};

const getTotalSize = ({ rowData: { totalSize } }) => (
  <span className={ styles.totalSize }>
    <Tooltip content="Total Size">
      <span>{ `USD ${ formatNumber(totalSize) }m` }</span>
    </Tooltip>
  </span>
);

const getIssuerTicker = ({ rowData: { issuerTicker } }) => (
  <span className={ styles.issuerTicker }>
    <Tooltip content="Issuer Ticker">
      <span>{ issuerTicker }</span>
    </Tooltip>
  </span>
);

// common columns
const ABBREVIATION_CELL = {
  name: 'abbreviation',
  title: '',
  sortable: false,
  width: 45,
  maxWidth: 45,
  minWidth: 45,
};

const TOTAL_SIZE_CELL = {
  name: 'totalSize',
  title: '',
  sortable: false,
  cellCallback: getTotalSize,
  width: 130,
  maxWidth: 130,
  minWidth: 130,
};

const NAME_CELL = {
  name: 'name',
  title: '',
  className: styles.nameWrp,
  sortable: false,
  cellCallback: getNameCell,
};

const DEAL_COUNT_CELL = {
  name: 'dealCount',
  title: '',
  sortable: false,
  cellCallback: getDealCount,
  width: 45,
  maxWidth: 45,
  minWidth: 45,
};

const LATEST_DEAL_DATE = {
  name: 'latestDealDate',
  title: '',
  sortable: false,
  cellCallback: getIssueDate,
  width: 85,
  maxWidth: 85,
  minWidth: 85,
};

const ISSUER_TICKER_CELL = {
  name: 'issuerTicker',
  title: '',
  sortable: false,
  cellCallback: getIssuerTicker,
  width: 65,
  maxWidth: 65,
  minWidth: 65,
};

// top issuers and top parents columns
export const TOP_ISSUERS_COLUMNS_WITH_TOTAL = [
  NAME_CELL,
  TOTAL_SIZE_CELL,
  DEAL_COUNT_CELL,
];

export const TOP_ISSUERS_COLUMNS_WITH_TOTAL_AND_TICKER = [
  ISSUER_TICKER_CELL,
  NAME_CELL,
  TOTAL_SIZE_CELL,
  DEAL_COUNT_CELL,
];

const TOP_ISSUERS_COLUMNS_WITHOUT_DATE_AND_TOTAL = [
  NAME_CELL,
  DEAL_COUNT_CELL,
];

export const TOP_ISSUERS_COLUMNS_WITH_DATE = [
  ISSUER_TICKER_CELL,
  NAME_CELL,
  DEAL_COUNT_CELL,
  LATEST_DEAL_DATE,
];

// top underwriters and rating agencies columns

export const TOP_UNDERWRITERS_AND_RATING_AGENCIES_COLUMNS_WITHOUT_TOTAL = [
  ABBREVIATION_CELL,
  NAME_CELL,
  DEAL_COUNT_CELL,
];

export const TOP_UNDERWRITERS_AND_RATING_AGENCIES_COLUMNS_WITH_TOTAL_SIZE = [
  ABBREVIATION_CELL,
  NAME_CELL,
  TOTAL_SIZE_CELL,
  DEAL_COUNT_CELL,
];

export const topCompaniesTablesCommonProps = {
  containerClassName: styles.container,
  tableClassName: styles.table,
  emptyMessageClassName: styles.emptyMessage,
  rowClassName: styles.row,
  showColumnsHeaders: false,
  isItemsPerPageVisible: false,
  withSpinner: false,
};

interface IGetTopCompanyColumns {
  showVolume: boolean,
  companyViewId?: string | string[],
  companyRole: string,
}

export const getTopCompanyColumns = ({
  showVolume,
  companyViewId,
  companyRole,
}: IGetTopCompanyColumns) => {
  switch (companyRole) {
    case RATING_AGENCY_NAME:
    case UNDERWRITER_NAME:
      if (showVolume) {
        return TOP_UNDERWRITERS_AND_RATING_AGENCIES_COLUMNS_WITH_TOTAL_SIZE;
      }
      return TOP_UNDERWRITERS_AND_RATING_AGENCIES_COLUMNS_WITHOUT_TOTAL;
    default:
      if (showVolume) {
        if (companyViewId) {
          return TOP_ISSUERS_COLUMNS_WITH_TOTAL_AND_TICKER;
        }
        return TOP_ISSUERS_COLUMNS_WITH_TOTAL;
      }
      if (companyViewId) {
        return TOP_ISSUERS_COLUMNS_WITH_DATE;
      }
      return TOP_ISSUERS_COLUMNS_WITHOUT_DATE_AND_TOTAL;
  }
};
